import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as api from '../api/api';
import { formatDate } from '../lib/format';
import { pickErrorMessage } from '../lib/apiHelpers';
import { toastError, toastSuccess } from '../lib/toast';
import { PageHeader, Card, CardBody, ErrorBanner, BackLink } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { ConfirmDialog } from '../components/Modal';

export default function BrandForm() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [brandName, setBrandName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [couponQuota, setCouponQuota] = useState(1000);
  const [isActive, setIsActive] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [quotaSummary, setQuotaSummary] = useState(null);
  const [quotaLedger, setQuotaLedger] = useState([]);
  const [grantAmount, setGrantAmount] = useState(100);
  const [grantNote, setGrantNote] = useState('');
  const [grantBusy, setGrantBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [error, setError] = useState('');

  const loadQuotaData = useCallback(async () => {
    if (!id || id === 'new') return;
    try {
      const [summary, ledger] = await Promise.all([
        api.getBrandQuotaSummary(id),
        api.listBrandQuotaLedger(id, { limit: 50 }),
      ]);
      setQuotaSummary(summary);
      setQuotaLedger(ledger?.items || []);
      if (summary?.remaining !== undefined) setCouponQuota(summary.remaining);
    } catch {
      /* optional on older backends */
    }
  }, [id]);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const row = await api.getBrand(id);
        if (!cancelled) {
          setBrandName(row.name || '');
          setEmail(row.email || '');
          setCouponQuota(row.couponQuota ?? 0);
          setIsActive(row.status === 'active');
        }
      } catch (e) {
        if (!cancelled) setError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  useEffect(() => {
    if (isNew) return;
    loadQuotaData();
  }, [isNew, loadQuotaData]);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (isNew) {
        await api.createBrand({
          brandName: brandName.trim(),
          name: name.trim(),
          email: email.trim(),
          password,
          couponQuota: Number(couponQuota) || 0,
        });
      } else {
        await api.updateBrand(id, {
          brandName: brandName.trim(),
          email: email.trim(),
          isActive,
        });
      }
      navigate('/brands');
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleGrantQuota(e) {
    e.preventDefault();
    const amount = Number(grantAmount);
    if (!amount || amount < 1) {
      toastError('Grant amount must be at least 1.');
      return;
    }
    setGrantBusy(true);
    setError('');
    try {
      const res = await api.grantBrandQuota(id, {
        amount,
        note: grantNote.trim() || undefined,
      });
      toastSuccess(res?.message || `Added ${amount} coupon quota.`);
      setGrantNote('');
      await loadQuotaData();
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setGrantBusy(false);
    }
  }

  async function confirmDeleteBrand() {
    setDeleteBusy(true);
    setError('');
    try {
      const res = await api.deleteBrand(id);
      toastSuccess(res?.message || 'Brand deleted.');
      setPendingDelete(false);
      navigate('/brands');
    } catch (err) {
      setError(pickErrorMessage(err));
      setPendingDelete(false);
    } finally {
      setDeleteBusy(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      toastError('New password must be at least 8 characters.');
      return;
    }
    setPasswordBusy(true);
    setError('');
    try {
      const res = await api.resetBrandPassword(id, { newPassword });
      toastSuccess(res?.message || 'Password reset successfully.');
      setNewPassword('');
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setPasswordBusy(false);
    }
  }

  if (loading) {
    return <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status" /></div>;
  }

  return (
    <>
      <BackLink to="/brands">Brands</BackLink>
      <PageHeader title={isNew ? 'Add brand' : 'Edit brand'} subtitle={isNew ? 'Create a new brand admin account' : brandName} />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <form onSubmit={handleSubmit} className="mw-form">
            <div className="mb-3">
              <label htmlFor="brandName" className="form-label">Brand name</label>
              <input id="brandName" className="form-control" value={brandName} onChange={(e) => setBrandName(e.target.value)} required />
            </div>
            {isNew ? (
              <>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Admin name</label>
                  <input id="name" className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">Login email</label>
                  <input id="email" type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="mb-3">
                  <label htmlFor="password" className="form-label">Password</label>
                  <input id="password" type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required />
                </div>
                <div className="mb-3">
                  <label htmlFor="quota" className="form-label">Initial coupon quota</label>
                  <input id="quota" type="number" min={0} className="form-control" value={couponQuota} onChange={(e) => setCouponQuota(e.target.value)} />
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">Login email</label>
                  <input
                    id="email"
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="quotaReadonly" className="form-label form-label-readonly">Remaining coupon quota</label>
                  <input
                    id="quotaReadonly"
                    type="number"
                    className="form-control form-control-readonly"
                    value={couponQuota}
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                  />
                  {quotaSummary ? (
                    <p className="form-text text-secondary mb-0">
                      Granted total: {quotaSummary.grantLimit} · Used: {quotaSummary.consumed} · Remaining: {quotaSummary.remaining}
                    </p>
                  ) : (
                    <p className="form-text text-secondary mb-0">
                      Remaining codes this brand can still generate. Use the section below to add more quota.
                    </p>
                  )}
                </div>
                <div className="form-check form-switch mb-4">
                  <input className="form-check-input" type="checkbox" id="active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                  <label className="form-check-label" htmlFor="active">Active</label>
                </div>
              </>
            )}
            <div className="d-flex gap-2 flex-wrap">
              <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              <Link to="/brands" className="btn btn-outline-secondary">Cancel</Link>
              {!isNew ? (
                <button
                  type="button"
                  className="btn btn-outline-danger ms-auto"
                  onClick={() => setPendingDelete(true)}
                  disabled={busy || deleteBusy}
                >
                  Delete brand
                </button>
              ) : null}
            </div>
          </form>
        </CardBody>
      </Card>
      {!isNew ? (
        <>
        <Card className="mb-3">
          <CardBody>
            <h2 className="h6 mb-3">Add coupon quota</h2>
            <p className="text-secondary small">Grant additional coupon codes. Each grant is recorded for audit.</p>
            <form onSubmit={handleGrantQuota} className="mw-form">
              <div className="row g-2">
                <div className="col-md-4">
                  <label htmlFor="grantAmount" className="form-label">Amount to add</label>
                  <input
                    id="grantAmount"
                    type="number"
                    min={1}
                    className="form-control"
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-8">
                  <label htmlFor="grantNote" className="form-label">Note (optional)</label>
                  <input
                    id="grantNote"
                    className="form-control"
                    value={grantNote}
                    onChange={(e) => setGrantNote(e.target.value)}
                    placeholder="e.g. Q2 top-up, pilot extension"
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary mt-3" disabled={grantBusy}>
                {grantBusy ? 'Adding…' : 'Add quota'}
              </button>
            </form>
          </CardBody>
        </Card>
        <Card className="mb-3">
          <CardBody>
            <h2 className="h6 mb-3">Quota grant history</h2>
            <DataTable
              columns={[
                { key: 'when', label: 'When', render: (r) => formatDate(r.createdAt) },
                { key: 'delta', label: 'Added', render: (r) => `+${r.delta}` },
                { key: 'balanceAfter', label: 'Remaining after' },
                { key: 'sourceType', label: 'Source', render: (r) => r.sourceType?.replace(/_/g, ' ') },
                { key: 'note', label: 'Note', render: (r) => r.note || '—' },
              ]}
              rows={quotaLedger}
              emptyTitle="No quota grants yet"
              emptyDescription="Initial creation and manual top-ups appear here."
              rowKey="ledgerId"
            />
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <h2 className="h6 mb-3">Reset brand password</h2>
            <p className="text-secondary small">Set a new login password for this brand admin.</p>
            <form onSubmit={handleResetPassword} className="mw-form">
              <div className="mb-3">
                <label htmlFor="newPassword" className="form-label">New password</label>
                <input
                  id="newPassword"
                  type="password"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  autoComplete="new-password"
                  required
                />
              </div>
              <button type="submit" className="btn btn-outline-primary" disabled={passwordBusy}>
                {passwordBusy ? 'Resetting…' : 'Reset password'}
              </button>
            </form>
          </CardBody>
        </Card>
        </>
      ) : null}
      {pendingDelete ? (
        <ConfirmDialog
          title="Delete brand?"
          message="This soft-deletes the brand and its coupon codes. Deletion is blocked while the brand still has unused (unscanned) coupon codes — remove those first."
          confirmLabel="Delete"
          danger
          busy={deleteBusy}
          onConfirm={confirmDeleteBrand}
          onCancel={() => setPendingDelete(false)}
        />
      ) : null}
    </>
  );
}
